/**
 * @license
 * Copyright (C) 2022 Anna Plodari
 * Software proprietario - Tutti i diritti riservati.
 * Registrato SIAE - Sezione OLAF.
 *
 * È vietato qualsiasi utilizzo non autorizzato, copia, modifica, estrazione,
 * riuso, rielaborazione, distribuzione, reverse engineering o decompilazione
 * del software, delle API, delle logiche applicative e dei dati del database.
 *
 * L'accesso al codice non costituisce concessione di licenza.
 */
import React, { useState } from 'react';
import * as yup from 'yup';
import { Control, useWatch } from 'react-hook-form';
import { regexBlocked, validateExtraField } from '../../lib/utils/validation';
import InputLg from '../../components/InputLg';

export const InputSchema = yup.object({
  scheda_tecnica: yup.object({
    macchine_utensili: yup.object({
      extra: validateExtraField(),
    }),
    saldatura: yup.object({
      extra: validateExtraField(),
    }),
    sistema_cnc: yup.object({
      extra: validateExtraField(),
    }),
    linguaggi_programmazione: yup.object({
      extra: validateExtraField(),
    }),
    linguaggi_progettazione: yup.object({
      extra: validateExtraField(),
    }),
    sistemi_meccatronici: yup.object({
      extra: validateExtraField(),
    }),
    disegno_tecnico: yup.object({
      extra: yup
        .string()
        .when('tipo', {
          is: 'Si',
          then: (schema) => schema.required('*Campo obbligatorio se selezioni "Si"'),
          otherwise: (schema) => schema.notRequired(),
        })
        .test('no-email', "Non puoi inserire l'email", (val) => !val || !regexBlocked.email.test(val))
        .test('no-phone', 'Non puoi inserireun  numero di telefono', (val) => !val || !regexBlocked.phone.test(val))
        .test('no-address', 'Non puoi inserire un indirizzo', (val) => !val || !regexBlocked.address.test(val)),
    }),
    disegno_meccanico: yup.object().shape({
      tipo: yup.array().of(yup.string()).nullable().notRequired(),
      extra: yup
        .string()
        .test('no-email', "Non puoi inserire l'email", (val?: string) => {
          if (!val) return true;
          return !regexBlocked.email.test(val);
        })
        .test('no-phone', 'Non puoi inserire un numero di telefono', (val?: string) => {
          if (!val) return true;
          return !regexBlocked.phone.test(val);
        })
        .test('no-address', 'Non puoi inserire un indirizzo', (val?: string) => {
          if (!val) return true;
          return !regexBlocked.address.test(val);
        })
        .concat(
          yup.string().when('tipo', {
            is: (val: string[] | undefined) => Array.isArray(val) && val.includes('altro'),
            then: (schema) => schema.required('*Campo obbligatorio se selezioni "Altro"'),
            otherwise: (schema) => schema.notRequired(),
          }),
        ),
    }),
    navigazione_internet: yup.object().shape({
      motori_ricerca: yup
        .object({
          tipo: yup.array().of(yup.string()).nullable().notRequired(),
          extra: yup
            .string()
            .when('tipo', {
              is: (val: string[] | undefined) => Array.isArray(val) && val.includes('altro'),
              then: (schema) => schema.required('*Campo obbligatorio se selezioni "Altro"'),
              otherwise: (schema) => schema.notRequired(),
            })
            .test('no-email', "Non puoi inserire l'email", (val?: string) => {
              if (!val) return true;
              return !regexBlocked.email.test(val);
            })
            .test('no-phone', 'Non puoi inserire un numero di telefono', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.phone.test(val);
            })
            .test('no-address', 'Non puoi inserire un indirizzo', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.address.test(val);
            }),
        })
        .nullable()
        .notRequired(),
      browser: yup
        .object({
          tipo: yup.array().of(yup.string()).nullable().notRequired(),
          extra: yup
            .string()
            .when('tipo', {
              is: (val: string[] | undefined) => Array.isArray(val) && val.includes('Altro'),
              then: (schema) => schema.required('*Campo obbligatorio se selezioni "Altro"'),
              otherwise: (schema) => schema.notRequired(),
            })
            .test('no-email', "Non puoi inserire l'email", (val?: string) => {
              if (!val) return true;
              return !regexBlocked.email.test(val);
            })
            .test('no-phone', 'Non puoi inserire un numero di telefono', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.phone.test(val);
            })
            .test('no-address', 'Non puoi inserire un indirizzo', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.address.test(val);
            }),
        })
        .nullable()
        .notRequired(),
      email: yup
        .object({
          tipo: yup.array().of(yup.string()).nullable().notRequired(),
          extra: yup
            .string()
            .when('tipo', {
              is: (val: string[] | undefined) => Array.isArray(val) && val.includes('Altro'),
              then: (schema) => schema.required('*Campo obbligatorio se selezioni "Altro"'),
              otherwise: (schema) => schema.notRequired(),
            })
            .test('no-email', "Non puoi inserire l'email", (val?: string) => {
              if (!val) return true;
              return !regexBlocked.email.test(val);
            })
            .test('no-phone', 'Non puoi inserire un numero di telefono', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.phone.test(val);
            })
            .test('no-address', 'Non puoi inserire un indirizzo', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.address.test(val);
            }),
        })
        .nullable()
        .notRequired(),
    }),
    gestione_database: yup.object().shape({
      db_relazionali: yup
        .object({
          tipo: yup.array().of(yup.string()).nullable().notRequired(),
          extra: yup
            .string()
            .when('tipo', {
              is: (val: string[] | undefined) => Array.isArray(val) && val.includes('altro'),
              then: (schema) => schema.required('*Campo obbligatorio se selezioni "Altro"'),
              otherwise: (schema) => schema.notRequired(),
            })
            .test('no-email', "Non puoi inserire l'email", (val?: string) => {
              if (!val) return true;
              return !regexBlocked.email.test(val);
            })
            .test('no-phone', 'Non puoi inserire un numero di telefono', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.phone.test(val);
            })
            .test('no-address', 'Non puoi inserire un indirizzo', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.address.test(val);
            }),
        })
        .nullable()
        .notRequired(),

      strumenti_gestione: yup
        .object({
          tipo: yup.array().of(yup.string()).nullable().notRequired(),
          extra: yup
            .string()
            .when('tipo', {
              is: (val: string[] | undefined) => Array.isArray(val) && val.includes('Altro'),
              then: (schema) => schema.required('*Campo obbligatorio se selezioni "Altro"'),
              otherwise: (schema) => schema.notRequired(),
            })
            .test('no-email', "Non puoi inserire l'email", (val?: string) => {
              if (!val) return true;
              return !regexBlocked.email.test(val);
            })
            .test('no-phone', 'Non puoi inserire un numero di telefono', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.phone.test(val);
            })
            .test('no-address', 'Non puoi inserire un indirizzo', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.address.test(val);
            }),
        })
        .nullable()
        .notRequired(),
    }),
    grafica_multimedia: yup.object().shape({
      montaggio_video: yup
        .object({
          tipo: yup.array().of(yup.string()).nullable().notRequired(),
          extra: yup
            .string()
            .when('tipo', {
              is: (val: string[] | undefined) => Array.isArray(val) && val.includes('altro'),
              then: (schema) => schema.required('*Campo obbligatorio se selezioni "Altro"'),
              otherwise: (schema) => schema.notRequired(),
            })
            .test('no-email', "Non puoi inserire l'email", (val?: string) => {
              if (!val) return true;
              return !regexBlocked.email.test(val);
            })
            .test('no-phone', 'Non puoi inserire un numero di telefono', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.phone.test(val);
            })
            .test('no-address', 'Non puoi inserire un indirizzo', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.address.test(val);
            }),
        })
        .nullable()
        .notRequired(),
    }),

    sviluppo_web: yup.object().shape({
      cms: yup
        .object({
          tipo: yup.array().of(yup.string()).nullable().notRequired(),

          extra: yup
            .string()
            .when('tipo', {
              is: (val: string[] | undefined) => Array.isArray(val) && val.includes('Altro'),
              then: (schema) => schema.required('*Campo obbligatorio se selezioni "Altro"'),
              otherwise: (schema) => schema.notRequired(),
            })
            .test('no-email', "Non puoi inserire l'email", (val?: string) => {
              if (!val) return true;
              return !regexBlocked.email.test(val);
            })
            .test('no-phone', 'Non puoi inserire un numero di telefono', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.phone.test(val);
            })
            .test('no-address', 'Non puoi inserire un indirizzo', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.address.test(val);
            }),
        })
        .nullable()
        .notRequired(),
    }),
    analisi_dati: yup.object().shape({
      strumenti_analisi: yup
        .object({
          tipo: yup.array().of(yup.string()).nullable().notRequired(),
          extra: yup
            .string()
            .when('tipo', {
              is: (val: string[] | undefined) => Array.isArray(val) && val.includes('Altro'),
              then: (schema) => schema.required('*Campo obbligatorio se selezioni "Altro"'),
              otherwise: (schema) => schema.notRequired(),
            })
            .test('no-email', "Non puoi inserire l'email", (val?: string) => {
              if (!val) return true;
              return !regexBlocked.email.test(val);
            })
            .test('no-phone', 'Non puoi inserire un numero di telefono', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.phone.test(val);
            })
            .test('no-address', 'Non puoi inserire un indirizzo', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.address.test(val);
            }),
        })
        .nullable()
        .notRequired(),
    }),
    intelligenza_artificiale: yup
      .object({
        intelligenza_artificiale: yup.array().of(yup.string()).nullable().notRequired(),
        extra: validateExtraField().nullable().notRequired(),
      })
      .nullable()
      .notRequired(),
    autotrasporto: yup.object().shape({
      tipo_patente: yup
        .string()
        .test('no-email', "Non puoi inserire l'email", (val?: string) => {
          if (!val) return true;
          return !regexBlocked.email.test(val);
        })
        .test('no-phone', 'Non puoi inserire un numero di telefono', (val?: string) => {
          if (!val) return true;
          return !regexBlocked.phone.test(val);
        })
        .test('no-address', 'Non puoi inserire un indirizzo', (val?: string) => {
          if (!val) return true;
          return !regexBlocked.address.test(val);
        }),
      tipo_merci: yup
        .string()
        .test('no-email', "Non puoi inserire l'email", (val?: string) => {
          if (!val) return true;
          return !regexBlocked.email.test(val);
        })
        .test('no-phone', 'Non puoi inserire un numero di telefono', (val?: string) => {
          if (!val) return true;
          return !regexBlocked.phone.test(val);
        })
        .test('no-address', 'Non puoi inserire un indirizzo', (val?: string) => {
          if (!val) return true;
          return !regexBlocked.address.test(val);
        }),
      tipo_veicoli: yup.object({
        tipo_veicoli: yup
          .object({
            tipo: yup.array().of(yup.string()).nullable().notRequired(),
            extra: yup
              .string()
              .when('tipo', {
                is: (val: string[] | undefined) => Array.isArray(val) && val.includes('altro'),
                then: (schema) => schema.required('*Campo obbligatorio se selezioni "Altro"'),
                otherwise: (schema) => schema.notRequired(),
              })
              .test('no-email', "Non puoi inserire l'email", (val?: string) => {
                if (!val) return true;
                return !regexBlocked.email.test(val);
              })
              .test('no-phone', 'Non puoi inserire un numero di telefono', (val?: string) => {
                if (!val) return true;
                return !regexBlocked.phone.test(val);
              })
              .test('no-address', 'Non puoi inserire un indirizzo', (val?: string) => {
                if (!val) return true;
                return !regexBlocked.address.test(val);
              }),
          })
          .nullable()
          .notRequired(),
      }),
    }),
    cantieristica_navale: yup.object().shape({
      cantieristica_navale: yup
        .object({
          tipo: yup.array().of(yup.string()).nullable().notRequired(),
          extra: yup
            .string()
            .when('tipo', {
              is: (val: string[] | undefined) => Array.isArray(val) && val.includes('altro'),
              then: (schema) => schema.required('*Campo obbligatorio se selezioni "Altro"'),
              otherwise: (schema) => schema.notRequired(),
            })
            .test('no-email', "Non puoi inserire l'email", (val?: string) => {
              if (!val) return true;
              return !regexBlocked.email.test(val);
            })
            .test('no-phone', 'Non puoi inserire un numero di telefono', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.phone.test(val);
            })
            .test('no-address', 'Non puoi inserire un indirizzo', (val?: string) => {
              if (!val) return true;
              return !regexBlocked.address.test(val);
            }),
        })
        .nullable()
        .notRequired(),
    }),
    verniciatura: yup.object().shape({
      tipo: yup.array().of(yup.string()).nullable().notRequired(),
      extra: yup
        .string()
        .when('tipo', {
          is: (val: string[] | undefined) => Array.isArray(val) && val.includes('altro'),
          then: (schema) => schema.required('*Campo obbligatorio se selezioni "Altro"'),
          otherwise: (schema) => schema.notRequired(),
        })
        .test('no-email', "Non puoi inserire l'email", (val?: string) => {
          if (!val) return true;
          return !regexBlocked.email.test(val);
        })
        .test('no-phone', 'Non puoi inserire un numero di telefono', (val?: string) => {
          if (!val) return true;
          return !regexBlocked.phone.test(val);
        })
        .test('no-address', 'Non puoi inserire un indirizzo', (val?: string) => {
          if (!val) return true;
          return !regexBlocked.address.test(val);
        }),
    }),
  }),
});

export function DisegnoTecnicoExtra({ control, errors }: { control: Control<any>, errors: any  }) {
  const tipo = useWatch({ control, name: 'scheda_tecnica.disegno_tecnico.tipo' });

  if (tipo === 'Si') {
    return (
      <InputLg
        name="scheda_tecnica.disegno_tecnico.extra"
        placeholder="Specifica il tipo di disegno tecnico"
        className="pl-[21px] text-sm text-left text-textgrey"
         error={errors.scheda_tecnica?.disegno_tecnico?.extra?.message}
      />
    );
  }

  return null;
}




export function NavigazioneInternetExtra({ control, errors  }: { control: Control<any>, errors: any}) {
  const tipo = useWatch({ control, name: 'scheda_tecnica.navigazione_internet.motori_ricerca.tipo' });

  if (Array.isArray(tipo) && (tipo.includes('Altro') || tipo.includes('altro'))) {
    return (
      <InputLg
        name="scheda_tecnica.navigazione_internet.motori_ricerca.extra"
        placeholder="Specifica il tipo di motore di ricerca"
        className="text-sm text-left text-textgrey pt-2"
        error={errors.scheda_tecnica?.navigazione_internet?.motori_ricerca?.extra?.message}
      />
    );
  }

  return null;
}

export function BrowserExtra({ control, errors  }: { control: Control<any>, errors: any}) {
  const tipo = useWatch({ control, name: 'scheda_tecnica.navigazione_internet.browser.tipo' });

if (Array.isArray(tipo) && (tipo.includes('Altro') || tipo.includes('altro'))) {
    return (
      <InputLg
        name="scheda_tecnica.navigazione_internet.browser.extra"
        placeholder="Specifica il tipo di browser"
        className="text-sm text-left text-textgrey pt-2"
        error={errors.scheda_tecnica?.navigazione_internet?.browser?.extra?.message}
      />
    );
  }

  return null;
}

export function EmailExtra({ control, errors  }: { control: Control<any>, errors: any}) {
  const tipo = useWatch({ control, name: 'scheda_tecnica.navigazione_internet.email.tipo' });
if (Array.isArray(tipo) && (tipo.includes('Altro') || tipo.includes('altro'))) {
    return (
      <InputLg
        name="scheda_tecnica.navigazione_internet.email.extra"
        placeholder="Specifica il tipo di email"
        className="text-sm text-left text-textgrey pt-2"
        error={errors.scheda_tecnica?.navigazione_internet?.email?.extra?.message}
      />
    );
  }

  return null;
}

export function DatabaseExtra({ control, errors  }: { control: Control<any>, errors: any}) {
  const tipo = useWatch({ control, name: 'scheda_tecnica.gestione_database.db_relazionali.tipo' });

  if (Array.isArray(tipo) && (tipo.includes('Altro') || tipo.includes('altro'))) {
    return (
      <InputLg
        name="scheda_tecnica.gestione_database.db_relazionali.extra"
        placeholder="Specifica il tipo di db relazionale"
        className="text-sm text-left text-textgrey pt-2"
        error={errors.scheda_tecnica?.gestione_database?.db_relazionali?.extra?.message}
      />
    );
  }

  return null;
}


export function MontaggioExtra({ control, errors  }: { control: Control<any>, errors: any}) {
  const tipo = useWatch({ control, name: 'scheda_tecnica.grafica_multimedia.montaggio_video.tipo' });

if (Array.isArray(tipo) && (tipo.includes('Altro') || tipo.includes('altro'))) {
    return (
      <InputLg
        name="scheda_tecnica.grafica_multimedia.montaggio_video.extra"
        placeholder="Specifica il tipo di montaggio video"
        className="text-sm text-left text-textgrey pt-2"
        error={errors.scheda_tecnica?.grafica_multimedia?.montaggio_video?.extra?.message}
      />
    );
  }

  return null;
}

export function CmsExtra({ control, errors  }: { control: Control<any>, errors: any}) {
  const tipo = useWatch({ control, name: 'scheda_tecnica.sviluppo_web.cms.tipo' });

if (Array.isArray(tipo) && (tipo.includes('Altro') || tipo.includes('altro'))) {
    return (
      <InputLg
        name="scheda_tecnica.sviluppo_web.cms.extra"
        placeholder="Specifica il tipo di cms"
        className="text-sm text-left text-textgrey pt-2"
        error={errors.scheda_tecnica?.sviluppo_web?.cms?.extra?.message}
      />
    );
  }

  return null;
}

export function StrumentiAnalisiExtra({ control, errors  }: { control: Control<any>, errors: any}) {
  const tipo = useWatch({ control, name: 'scheda_tecnica.analisi_dati.strumenti_analisi.tipo' });

 if (Array.isArray(tipo) && (tipo.includes('Altro') || tipo.includes('altro'))) {
    return (
      <InputLg
        name="scheda_tecnica.analisi_dati.strumenti_analisi.extra"
        placeholder="Specifica il tipo di strumento di analisi"
        className="text-sm text-left text-textgrey pt-2"
        error={errors.scheda_tecnica?.analisi_dati?.strumenti_analisi?.extra?.message}
      />
    );
  }

  return null;
}

export function TipoVeicoliExtra({ control, errors  }: { control: Control<any>, errors: any}) {
  const tipo = useWatch({ control, name: 'scheda_tecnica.autotrasporto.tipo_veicoli.tipo_veicoli.tipo' });

 if (Array.isArray(tipo) && (tipo.includes('Altro') || tipo.includes('altro'))) {
    return (
      <InputLg
        name="scheda_tecnica.autotrasporto.tipo_veicoli.tipo_veicoli.extra"
        placeholder="Specifica il tipo di veicolo"
        className="text-sm text-left text-textgrey pt-2"
        error={errors.scheda_tecnica?.autotrasporto?.tipo_veicoli?.tipo_veicoli?.extra?.message}
      />
    );
  }

  return null;
}

export function CantieristicaNavaleExtra({ control, errors  }: { control: Control<any>, errors: any}) {
  const tipo = useWatch({ control, name: 'scheda_tecnica.cantieristica_navale.cantieristica_navale.tipo' });

  if (Array.isArray(tipo) && (tipo.includes('Altro') || tipo.includes('altro'))) {
    return (
      <InputLg
        name="scheda_tecnica.cantieristica_navale.cantieristica_navale.extra"
        placeholder="Specifica il tipo di cantieristica navale"
        className="text-sm text-left text-textgrey pt-2"
        error={errors.scheda_tecnica?.cantieristica_navale?.cantieristica_navale?.extra?.message}
      />
    );
  }

  return null;
}
